import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { ReactComponent as SettingsIcon } from "../Logos To be used/3.svg";
import { ReactComponent as LogoutIcon } from "../Logos To be used/7.svg";
import { ReactComponent as EditIcon } from "../Logos To be used/8.svg";

// Import lock icon for unfinished sections
import { ReactComponent as LockIcon } from "../Logos To be used/lock.svg";

import "./../styles/Menu.css";
import { clearAuthSession } from "../utils/authSession";

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18px" height="18px">
    <path d="M12 16l-6-6h12z" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18px" height="18px">
    <path d="M12 8l-6 6h12z" />
  </svg>
);

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      name
      email
    }
  }
`;

const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      id
    }
  }
`;

const Menu = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuEntries = [
    {
      key: "Einstellungen",
      hash: "settings",
      Icon: SettingsIcon,
      locked: false,
    },
    {
      key: "Meine Fächer",
      hash: "subjects",
      Icon: LockIcon,
      locked: true,
    },
    {
      key: "Gespeicherte Lektionen",
      hash: "lessons",
      Icon: LockIcon,
      locked: true,
    },
    {
      key: "Alt Klausuren",
      hash: "exams",
      Icon: LockIcon,
      locked: true,
    },
  ];

  const [selectedMenu, setSelectedMenu] = useState("Einstellungen");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [username, setUsername] = useState("");
  const [usernameDraft, setUsernameDraft] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState({});
  const modalRef = useRef(null);

  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const [changePassword] = useMutation(CHANGE_PASSWORD_MUTATION);

  useEffect(() => {
    const hash = location.hash.substring(1);
    switch (hash) {
      case "settings":
        setSelectedMenu("Einstellungen");
        break;
      case "subjects":
        setSelectedMenu("Meine Fächer");
        break;
      case "lessons":
        setSelectedMenu("Gespeicherte Lektionen");
        break;
      case "exams":
        setSelectedMenu("Alt Klausuren");
        break;
      default:
        setSelectedMenu("Einstellungen");
    }
  }, [location]);

  useEffect(() => {
    const storedName = localStorage.getItem("userName");
    const storedEmail = localStorage.getItem("userEmail");
    if (storedName) {
      setUsername(storedName);
      setUsernameDraft(storedName);
    }
    if (storedEmail) setEmail(storedEmail);
  }, []);

  const handleMenuClick = (menu) => {
    setSelectedMenu(menu);
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const closeEdit = useCallback(() => {
    setIsEditing(null);
    setError({});
    setUsernameDraft(username);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }, [username]);

  const openEdit = useCallback(
    (mode) => {
      setIsEditing(mode);
      setError({});
      if (mode === "username") {
        setUsernameDraft(username);
        return;
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    },
    [username]
  );

  const handleOutsideClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeEdit();
      }
    },
    [closeEdit]
  );

  useEffect(() => {
    if (isEditing) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isEditing, handleOutsideClick]);

  const handleSaveChanges = async () => {
    try {
      const userId = localStorage.getItem("userID");

      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }

      if (isEditing === "password") {
        if (!currentPassword) {
          setError({ currentPassword: "Aktuelles Kennwort darf nicht leer sein." });
          return;
        }

        if (newPassword.length < 8) {
          setError({ newPassword: "Neues Kennwort muss mindestens 8 Zeichen haben." });
          return;
        }

        if (newPassword !== confirmPassword) {
          setError({ confirmPassword: "Neue Kennwörter stimmen nicht überein." });
          return;
        }

        await changePassword({
          variables: {
            input: { oldPassword: currentPassword, newPassword },
          },
        });

        closeEdit();
      } else if (isEditing === "username") {
        const sanitizedUsername = usernameDraft.trim();
        if (!sanitizedUsername) {
          setError({ username: "Benutzername darf nicht leer sein." });
          return;
        }

        const { data } = await updateUser({
          variables: {
            input: { id: userId, name: sanitizedUsername },
          },
        });

        const savedUsername = data?.updateUser?.name || sanitizedUsername;
        setUsername(savedUsername);
        setUsernameDraft(savedUsername);
        localStorage.setItem("userName", savedUsername);
        closeEdit();
      }
    } catch (err) {
      console.error("Error updating user:", err);
      const message = err?.graphQLErrors?.[0]?.message || err?.message || "";

      if (isEditing === "password") {
        if (/old|current|falsch|invalid/i.test(message)) {
          setError({ currentPassword: "Aktuelles Kennwort ist falsch." });
          return;
        }
        setError({ form: message || "Kennwort konnte nicht geändert werden." });
        return;
      }

      if (isEditing === "username") {
        setError({ username: message || "Benutzername konnte nicht gespeichert werden." });
      }
    }
  };

  const renderEditModal = () => {
    if (!isEditing) return null;

    return (
      <div className="lh-menu-modal-overlay">
        <div className="lh-menu-modal" ref={modalRef} role="dialog" aria-modal="true">
          <div className="lh-menu-modal-header">
            <div>
              <h3 className="lh-menu-modal-title">
                {isEditing === "username" ? "Benutzername ändern" : "Kennwort ändern"}
              </h3>
              <p className="lh-menu-modal-subtitle">
                Aktualisiere deine Kontoinformationen sicher.
              </p>
            </div>
            <button
              className="lh-menu-modal-close"
              type="button"
              aria-label="Schließen"
              onClick={closeEdit}
            >
              ×
            </button>
          </div>

          <div className="lh-menu-modal-content">
          {isEditing === "username" && (
            <>
              <label className="lh-menu-field-label" htmlFor="menu-username-input">
                Benutzername
              </label>
              <input
                id="menu-username-input"
                className={`lh-menu-input ${error.username ? "error" : ""}`}
                placeholder="Neue Benutzername*"
                value={usernameDraft}
                onChange={(e) => {
                  setUsernameDraft(e.target.value);
                  setError((prev) => ({ ...prev, username: undefined }));
                }}
              />
              {error.username && <p className="lh-menu-error">{error.username}</p>}
              <button
                className="lh-menu-submit"
                type="button"
                onClick={handleSaveChanges}
              >
                Änderung bestätigen
              </button>
            </>
          )}
          {isEditing === "password" && (
            <>
              <label className="lh-menu-field-label" htmlFor="menu-current-password">
                Aktuelles Kennwort
              </label>
              <div className="lh-menu-password-wrap">
                <input
                  id="menu-current-password"
                  className={`lh-menu-input lh-menu-input-with-toggle ${
                    error.currentPassword ? "error" : ""
                  }`}
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Aktuelles Kennwort*"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setError((prev) => ({
                      ...prev,
                      currentPassword: undefined,
                      form: undefined,
                    }));
                  }}
                />
                <button
                  className="lh-menu-visibility-toggle"
                  type="button"
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                  aria-label={
                    showCurrentPassword ? "Aktuelles Kennwort verbergen" : "Aktuelles Kennwort anzeigen"
                  }
                >
                  {showCurrentPassword ? "Verbergen" : "Anzeigen"}
                </button>
              </div>
              {error.currentPassword && (
                <p className="lh-menu-error">{error.currentPassword}</p>
              )}

              <label className="lh-menu-field-label" htmlFor="menu-new-password">
                Neues Kennwort
              </label>
              <div className="lh-menu-password-wrap">
                <input
                  id="menu-new-password"
                  className={`lh-menu-input lh-menu-input-with-toggle ${
                    error.newPassword ? "error" : ""
                  }`}
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Neues Kennwort* (mind. 8 Zeichen)"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError((prev) => ({
                      ...prev,
                      newPassword: undefined,
                      confirmPassword: undefined,
                      form: undefined,
                    }));
                  }}
                />
                <button
                  className="lh-menu-visibility-toggle"
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? "Neues Kennwort verbergen" : "Neues Kennwort anzeigen"}
                >
                  {showNewPassword ? "Verbergen" : "Anzeigen"}
                </button>
              </div>
              {error.newPassword && <p className="lh-menu-error">{error.newPassword}</p>}

              <label className="lh-menu-field-label" htmlFor="menu-confirm-password">
                Kennwort wiederholen
              </label>
              <div className="lh-menu-password-wrap">
                <input
                  id="menu-confirm-password"
                  className={`lh-menu-input lh-menu-input-with-toggle ${
                    error.confirmPassword ? "error" : ""
                  }`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Wiederholen neues Kennwort*"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                      form: undefined,
                    }));
                  }}
                />
                <button
                  className="lh-menu-visibility-toggle"
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={
                    showConfirmPassword ? "Kennwortbestätigung verbergen" : "Kennwortbestätigung anzeigen"
                  }
                >
                  {showConfirmPassword ? "Verbergen" : "Anzeigen"}
                </button>
              </div>
              {error.confirmPassword && (
                <p className="lh-menu-error">{error.confirmPassword}</p>
              )}
              {error.form && <p className="lh-menu-error">{error.form}</p>}
              <button
                className="lh-menu-submit"
                type="button"
                onClick={handleSaveChanges}
              >
                Änderung bestätigen
              </button>
            </>
          )}
          </div>
        </div>
      </div>
    );
  };

  const isSettingsSelected = selectedMenu === "Einstellungen";

  return (
    <div className="lh-menu-page">
      <div className="lh-menu-shell">
        <div className="lh-menu-responsive">
          <button className="lh-menu-responsive-button" type="button" onClick={toggleDropdown}>
            <span>Settings Navigation</span>
            {showDropdown ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </button>

          {showDropdown && (
            <div className="lh-menu-responsive-dropdown">
              {menuEntries.map(({ key, hash, Icon, locked }) => (
                locked ? (
                  <button
                    key={key}
                    type="button"
                    className={`lh-menu-item ${selectedMenu === key ? "active" : ""} locked`}
                    aria-disabled="true"
                  >
                    <Icon className="icon" />
                    <span>{key}</span>
                  </button>
                ) : (
                  <Link
                    key={key}
                    className={`lh-menu-item ${selectedMenu === key ? "active" : ""}`}
                    to={`/menu#${hash}`}
                    onClick={() => handleMenuClick(key)}
                  >
                    <Icon className="icon" />
                    <span>{key}</span>
                  </Link>
                )
              ))}
            </div>
          )}
        </div>

        <div className="lh-menu-layout">
          <aside className="lh-menu-sidebar">
            <div className="lh-menu-sidebar-head">
              <p className="lh-menu-eyebrow">Kontoverwaltung</p>
              <h1>Settings</h1>
            </div>

            <nav className="lh-menu-nav">
              {menuEntries.map(({ key, hash, Icon, locked }) => (
                locked ? (
                  <button
                    key={key}
                    type="button"
                    className={`lh-menu-item ${selectedMenu === key ? "active" : ""} locked`}
                    aria-disabled="true"
                  >
                    <Icon className="icon" />
                    <span>{key}</span>
                  </button>
                ) : (
                  <Link
                    key={key}
                    className={`lh-menu-item ${selectedMenu === key ? "active" : ""}`}
                    to={`/menu#${hash}`}
                    onClick={() => handleMenuClick(key)}
                  >
                    <Icon className="icon" />
                    <span>{key}</span>
                  </Link>
                )
              ))}
            </nav>

            <div className="lh-menu-sidebar-foot">
              <button
                className="lh-menu-logout"
                type="button"
                onClick={() => {
                  clearAuthSession();
                  navigate("/", { replace: true });
                }}
              >
                <LogoutIcon className="icon" />
                <span>Abmelden</span>
              </button>
            </div>
          </aside>

          <section className="lh-menu-main">
            <header className="lh-menu-main-header">
              <div>
                <p className="lh-menu-eyebrow">Workspace</p>
                <h2>{selectedMenu}</h2>
              </div>
            </header>

            {isSettingsSelected ? (
              <div className="lh-settings-grid">
                <article className="lh-setting-card">
                  <div className="lh-setting-copy">
                    <p className="lh-setting-label">Academic Email</p>
                    <p className="lh-setting-value">{email || "example@gmail.com"}</p>
                    <p className="lh-setting-note">
                      Verifiziertes Konto für Benachrichtigungen und Support.
                    </p>
                  </div>
                </article>

                <article className="lh-setting-card">
                  <div className="lh-setting-copy">
                    <p className="lh-setting-label">Benutzername</p>
                    <p className="lh-setting-value">{username || "example User"}</p>
                    <p className="lh-setting-note">
                      Dieser Name wird in Kursen und Diskussionen angezeigt.
                    </p>
                  </div>
                  <button
                    className="lh-setting-action"
                    type="button"
                    onClick={() => openEdit("username")}
                  >
                    <EditIcon className="edit-icon" />
                    <span>Bearbeiten</span>
                  </button>
                </article>

                <article className="lh-setting-card">
                  <div className="lh-setting-copy">
                    <p className="lh-setting-label">Kennwort</p>
                    <p className="lh-setting-value">••••••••</p>
                    <p className="lh-setting-note">
                      Ändere dein Kennwort regelmäßig für mehr Sicherheit.
                    </p>
                  </div>
                  <button
                    className="lh-setting-action"
                    type="button"
                    onClick={() => openEdit("password")}
                  >
                    <EditIcon className="edit-icon" />
                    <span>Bearbeiten</span>
                  </button>
                </article>

                <article className="lh-setting-card">
                  <div className="lh-setting-copy">
                    <p className="lh-setting-label">Universität</p>
                    <p className="lh-setting-value">OVGU Magdeburg</p>
                    <p className="lh-setting-note">
                      Institutionelle Angaben werden zentral verwaltet.
                    </p>
                  </div>
                  <span className="lh-setting-chip">Verwaltet</span>
                </article>
              </div>
            ) : (
              <div className="lh-menu-locked-panel">
                <LockIcon className="lh-menu-locked-icon" />
                <h3>Dieser Bereich ist aktuell nicht verfügbar</h3>
                <p>
                  Die Sektion <strong>{selectedMenu}</strong> ist sichtbar, aber derzeit
                  gesperrt.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
      {renderEditModal()}
    </div>
  );
};

export default Menu;
